import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Heart, Users, Star, ArrowRight, ChevronLeft, ChevronRight, X } from 'lucide-react';

// Shop photos
const shopPhotos = [
  {
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/joaxdv9x_66a90a91-a592-4149-8024-e64ef86cd0ee.jpg',
    alt: 'BraidsByMel Lounge Area'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/7ykelztm_87dbeaed-bf54-4f25-a94b-f9fdca0a8a7a.jpg',
    alt: 'BraidsByMel Styling Stations'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/f5o0nzux_99be4340-1fb6-4abe-82ac-7bded5c8ad54.jpg',
    alt: 'BraidsByMel Salon Interior'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/xg0picgu_89519dba-5f04-4005-a2c0-82a47e8284b5.jpg',
    alt: 'BraidsByMel Full Studio View'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_mel-salon-luxury/artifacts/uwhj8wc4_3116297e-ec99-47ad-9e4c-9e427d3bc936.jpg',
    alt: 'BraidsByMel Reception Area'
  }
];

const stats = [
  { number: '500+', label: 'Happy Clients', icon: Users },
  { number: '10+', label: 'Years Experience', icon: Award },
  { number: '5', label: 'Star Rating', icon: Star },
  { number: '100%', label: 'Satisfaction', icon: Heart },
];

// Studio Slideshow Component
const StudioSlideshow = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % shopPhotos.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + shopPhotos.length) % shopPhotos.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % shopPhotos.length);
  };

  const openModal = (index) => {
    setModalIndex(index);
    setIsModalOpen(true);
  };

  const modalPrev = () => {
    setModalIndex((prev) => (prev - 1 + shopPhotos.length) % shopPhotos.length);
  };

  const modalNext = () => {
    setModalIndex((prev) => (prev + 1) % shopPhotos.length);
  };

  return (
    <>
      <section className="py-24 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-script text-4xl md:text-6xl text-gold mb-4">Our Studio</h2>
            <p className="font-sans text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              Step into luxury. Our salon is designed to make you feel like royalty from the moment you walk in.
            </p>
          </motion.div>

          {/* Main Slideshow */}
          <div className="relative max-w-5xl mx-auto mb-8">
            <div className="aspect-[16/10] relative overflow-hidden rounded-2xl border-2 border-gold/30">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={shopPhotos[currentIndex].src}
                  alt={shopPhotos[currentIndex].alt}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => openModal(currentIndex)}
                />
              </AnimatePresence>
              
              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-gold/50 flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
                data-testid="slideshow-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-gold/50 flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all"
                data-testid="slideshow-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Click to expand hint */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-gold font-sans text-sm">Click to expand</span>
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-3 mt-6">
              {shopPhotos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-pink w-8' 
                      : 'bg-gold/40 hover:bg-gold'
                  }`}
                  data-testid={`dot-${index}`}
                />
              ))}
            </div>
          </div>

          {/* Thumbnails */}
          <div className="flex justify-center gap-4 flex-wrap">
            {shopPhotos.map((photo, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentIndex(index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                  index === currentIndex 
                    ? 'border-pink shadow-[0_0_20px_rgba(255,20,147,0.4)]' 
                    : 'border-gold/30 hover:border-gold'
                }`}
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-full object-cover"
                />
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <button
              className="absolute top-6 right-6 text-gold hover:text-pink transition-colors z-10"
              onClick={() => setIsModalOpen(false)}
              data-testid="close-modal"
            >
              <X size={32} />
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); modalPrev(); }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm border border-gold/50 flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            <motion.div
              key={modalIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={shopPhotos[modalIndex].src}
                alt={shopPhotos[modalIndex].alt}
                className="w-full h-auto max-h-[85vh] object-contain rounded-xl"
              />
            </motion.div>

            <button
              onClick={(e) => { e.stopPropagation(); modalNext(); }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm border border-gold/50 flex items-center justify-center text-gold hover:bg-pink hover:border-pink hover:text-white transition-all z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Modal Dots */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
              {shopPhotos.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setModalIndex(index); }}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === modalIndex 
                      ? 'bg-pink w-8' 
                      : 'bg-gold/40 hover:bg-gold'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const About = () => {
  return (
    <div className="page-transition pt-24">
      {/* Hero */}
      <section className="py-20 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-gold">
                <img
                  src="https://images.pexels.com/photos/5957103/pexels-photo-5957103.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800"
                  alt="BraidsByMel"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-pink px-8 py-4 rounded-xl">
                <span className="font-script text-3xl text-white">Est. 2015</span>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <h1 className="font-script text-5xl md:text-7xl text-gold mb-6">
                The Story Behind BraidsByMel
              </h1>
              <p className="font-serif text-xl text-gold-light italic mb-6">
                The Philippines' Premier Black-Owned Hair Salon
              </p>
              <div className="space-y-4 text-[#A3A3A3] font-sans leading-relaxed">
                <p>
                  BraidsByMel is proudly the best Black-owned hair business in the Philippines, 
                  bringing authentic African braiding techniques and professional haircut artistry 
                  to Metro Manila. Founded with a vision to share the beauty and cultural richness 
                  of African hairstyling, we've become a sanctuary for everyone seeking excellence.
                </p>
                <p>
                  <span className="text-pink font-semibold">We welcome ALL people</span> — every race, 
                  nationality, age, and gender. Our doors are open to Filipinos, expats, tourists, 
                  and anyone who appreciates the art of authentic braiding and precision cuts. 
                  Diversity is our strength, and every client is treated like family.
                </p>
                <p>
                  Our talented team of skilled braiders and barbers are trained in traditional 
                  African techniques passed down through generations. From intricate cornrows to 
                  stunning knotless braids, from sharp fades to creative hair designs — we deliver 
                  results that turn heads and boost confidence.
                </p>
                <p>
                  Located in the heart of Makati City, BraidsByMel has become the go-to destination 
                  for those who refuse to settle for anything less than authentic, professional, 
                  and luxurious hair services.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-6 bg-surface">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                  <stat.icon className="w-8 h-8 text-pink" />
                </div>
                <span className="font-script text-4xl md:text-5xl text-gold block mb-2">
                  {stat.number}
                </span>
                <span className="font-sans text-[#A3A3A3]">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-24 px-6 bg-[#050505]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-script text-4xl md:text-6xl text-gold mb-4">Our Philosophy</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Authentic African Braiding',
                description: 'Traditional techniques passed down through generations. Real African braids done by skilled professionals who understand the culture and craft.'
              },
              {
                title: 'Inclusive & Welcoming',
                description: 'We celebrate diversity. All races, nationalities, ages, and genders are welcome. Everyone deserves to feel beautiful and confident.'
              },
              {
                title: 'Expert Team',
                description: 'Our talented barbers and braiders are trained in both traditional African styles and modern techniques. Precision and artistry in every service.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="bg-surface border border-gold/20 rounded-2xl p-8 text-center"
              >
                <h3 className="font-serif text-2xl text-gold-light mb-4">{item.title}</h3>
                <p className="text-[#A3A3A3] font-sans">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Studio - Slideshow */}
      <StudioSlideshow />

      {/* Celebrity Trust */}
      <section className="py-24 px-6 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink/5 via-transparent to-gold/5" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-script text-4xl md:text-6xl text-gold mb-6">
              Everyone Is Welcome Here
            </h2>
            <p className="font-serif text-xl text-gold-light italic mb-8">
              "Your hair, your crown — we're here to make it shine."
            </p>
            <p className="text-[#A3A3A3] font-sans leading-relaxed max-w-2xl mx-auto mb-6">
              Whether you're a local Filipino, an expat living in Manila, a tourist exploring 
              the Philippines, or anyone in between — BraidsByMel is YOUR salon. We take pride 
              in being the most inclusive hair destination in the country.
            </p>
            <p className="text-[#A3A3A3] font-sans leading-relaxed max-w-2xl mx-auto mb-10">
              <span className="text-pink">Visit us at:</span><br />
              <span className="text-[#F5F5F0]">5682 Doña Carmen Street, Poblacion, Makati City, 1210 Metro Manila</span>
            </p>
            <Link
              to="/contact"
              className="btn-gold px-10 py-4 rounded-full font-serif text-lg inline-flex items-center gap-2 group"
              data-testid="about-book-btn"
            >
              Book Your Appointment
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
